/**
 * Change password — reached from the profile menu.
 *
 * New password + confirmation, checked here and again by the server
 * (`POST /account/password`, which updates the Sangnan account as the caller).
 * The session stays valid after the change, so the writer is not signed out.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  makeStyles,
} from "@fluentui/react-components";
import { type FC, type FormEvent, useState } from "react";

import { changePassword } from "../../api/client";
import { fonts, inkColors } from "../tokens";

/** Mirrors `MIN_PASSWORD_LENGTH` in server/routes/account.py. */
const MIN_PASSWORD_LENGTH = 8;

const useStyles = makeStyles({
  surface: { maxWidth: "380px", width: "calc(100vw - 32px)" },
  body: { display: "flex", flexDirection: "column", gap: "12px" },
  done: { fontFamily: fonts.body, fontSize: "13px", color: inkColors.ink },
});

/** The first thing wrong with the pair, or null when it may be submitted. */
export function passwordProblem(next: string, confirm: string): string | null {
  if (next.length < MIN_PASSWORD_LENGTH) return `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  if (next !== confirm) return "Passwords do not match.";
  return null;
}

export const ChangePasswordDialog: FC<{ onDismiss: () => void }> = ({ onDismiss }) => {
  const styles = useStyles();
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const problem = passwordProblem(next, confirm);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    setTouched(true);
    if (problem !== null || busy) return;
    setBusy(true);
    setError(null);
    try {
      await changePassword(next, confirm);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "Could not change your password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(_, data) => {
        if (!data.open) onDismiss();
      }}
    >
      <DialogSurface className={styles.surface}>
        <form onSubmit={(e) => void submit(e)}>
          <DialogBody>
            <DialogTitle>Change password</DialogTitle>
            <DialogContent>
              {done ? (
                <span className={styles.done}>Your password has been changed.</span>
              ) : (
                <div className={styles.body}>
                  <Field label="New password" required>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      value={next}
                      onChange={(_, d) => setNext(d.value)}
                      disabled={busy}
                    />
                  </Field>
                  <Field
                    label="Re-enter new password"
                    required
                    validationState={(touched && problem !== null) || error !== null ? "error" : "none"}
                    validationMessage={touched && problem !== null ? problem : (error ?? undefined)}
                  >
                    <Input
                      type="password"
                      autoComplete="new-password"
                      value={confirm}
                      onChange={(_, d) => setConfirm(d.value)}
                      disabled={busy}
                    />
                  </Field>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              {done ? (
                <Button appearance="primary" onClick={onDismiss}>
                  Close
                </Button>
              ) : (
                <>
                  <Button appearance="secondary" onClick={onDismiss} disabled={busy}>
                    Cancel
                  </Button>
                  <Button appearance="primary" type="submit" disabled={busy}>
                    {busy ? "Saving…" : "Change password"}
                  </Button>
                </>
              )}
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
};

export default ChangePasswordDialog;
